/**
 * Aligns free-text search with storefront facets and category collections.
 *
 * Shopify predictive search ignores metafield filters, and semantic storefront
 * search mishandles many Danish terms left in `q` (colors AND category words
 * like "toppe"). When a query contains facet labels and/or matches a collection:
 *  1. Strip matched facet terms from `q` and apply filter.* params
 *  2. If the remaining category term matches a collection (as the drawer shows),
 *     go to that collection (with any filters) instead of /search
 *  3. Use filtered /search for the drawer when facets are detected
 *
 * Examples:
 *  - "grøn kjole" → /search?q=kjole&filter.p.m.custom.color_filter=Grøn
 *  - "blå" → /search?q=*&filter.p.m.custom.color_filter=Blå
 *  - "toppe" → /search?q=top&search_collection=toppe
 *  - "sort toppe" → /search?q=top&search_collection=toppe&filter.p.m.custom.color_filter=Sort
 */
(() => {
  const TERMS_SCRIPT_ID = "search-filter-terms";
  const DISPLAY_QUERY_KEY = "searchDisplayQuery";
  const MIN_PHRASE_LENGTH = 3;

  // Alias token → normalizeLabel(facet label). Must match normalizeToken output.
  const LABEL_ALIASES = {
    sort: "sort",
    sorte: "sort",
    sorten: "sort",
    hvid: "hvid",
    hvide: "hvid",
    hvidt: "hvid",
    brun: "brun",
    brune: "brun",
    brunt: "brun",
    sand: "sand",
    sandfarvet: "sand",
    gra: "gra",
    graa: "gra",
    rod: "roed",
    rode: "roed",
    roed: "roed",
    roede: "roed",
    bla: "bla",
    blaa: "bla",
    blae: "bla",
    gul: "gul",
    gule: "gul",
    gult: "gul",
    gron: "groen",
    groen: "groen",
    gronne: "groen",
    groenne: "groen",
    pink: "pink",
    pinke: "pink",
    orange: "orange",
    lilla: "lilla",
    denim: "denim",
    stribet: "stribet",
    stribede: "stribet",
    print: "print",
    prikket: "prikket",
    prikkede: "prikket",
    ternet: "ternet",
    ternede: "ternet",
    bordeaux: "bordeaux",
  };

  /** @type {{ param: string, value: string, label: string, tokens: string[] }[] | null} */
  let phraseIndex = null;
  let loadingIndex = null;

  function normalizeToken(token) {
    return token
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/æ/g, "ae")
      .replace(/ø/g, "oe")
      .replace(/å/g, "aa");
  }

  function normalizeLabel(label) {
    return normalizeToken(
      String(label || "")
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, "&"),
    );
  }

  function decodeHtmlEntities(value) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
  }

  function shouldIncludeFilterParam(param) {
    if (!param || param === "filter.v.availability") return false;
    if (param.startsWith("filter.v.option.")) return false;
    return param.startsWith("filter.");
  }

  function buildPhraseIndex(filters) {
    /** @type {Map<string, { param: string, value: string, label: string, tokens: string[] }>} */
    const byKey = new Map();

    for (const filter of filters || []) {
      if (!shouldIncludeFilterParam(filter.param)) continue;

      for (const entry of filter.values || []) {
        const label = decodeHtmlEntities(entry.label || entry.value || "");
        const value = decodeHtmlEntities(entry.value || entry.label || "");
        if (!label) continue;

        const tokens = normalizeLabel(label).split(/\s+/).filter(Boolean);
        if (tokens.length === 0) continue;
        if (tokens.join("").length < MIN_PHRASE_LENGTH && tokens.length === 1) continue;

        const phrase = {
          param: filter.param,
          value,
          label,
          tokens,
        };
        byKey.set(`${filter.param}::${normalizeLabel(label)}`, phrase);

        // Register Danish aliases that resolve to this label (colors etc.).
        const labelKey = tokens.join(" ");
        for (const [alias, target] of Object.entries(LABEL_ALIASES)) {
          if (target === labelKey) {
            byKey.set(`${filter.param}::alias::${alias}`, {
              ...phrase,
              tokens: [alias],
            });
          }
        }
      }
    }

    return [...byKey.values()].sort((a, b) => b.tokens.length - a.tokens.length || b.tokens.join("").length - a.tokens.join("").length);
  }

  function readEmbeddedFilters() {
    const el = document.getElementById(TERMS_SCRIPT_ID);
    if (!el) return [];
    try {
      const data = JSON.parse(el.textContent || "{}");
      return Array.isArray(data.filters) ? data.filters : [];
    } catch (e) {
      return [];
    }
  }

  async function fetchFiltersFromCollection() {
    const root = window.Shopify?.routes?.root || "/";
    const html = await (await fetch(`${root}collections/all`)).text();
    /** @type {Map<string, { param: string, label: string, values: { value: string, label: string }[] }>} */
    const byParam = new Map();
    const inputRe = /name="(filter\.[^"]+)"[^>]*value="([^"]+)"/gi;
    let match;
    while ((match = inputRe.exec(html))) {
      const param = match[1];
      const value = decodeHtmlEntities(match[2]);
      if (!shouldIncludeFilterParam(param)) continue;
      if (!byParam.has(param)) {
        byParam.set(param, { param, label: param, values: [] });
      }
      const bucket = byParam.get(param);
      if (!bucket.values.some((entry) => entry.value === value)) {
        bucket.values.push({ value, label: value });
      }
    }
    return [...byParam.values()];
  }

  async function ensurePhraseIndex() {
    if (phraseIndex) return phraseIndex;
    if (loadingIndex) return loadingIndex;

    loadingIndex = (async () => {
      let filters = readEmbeddedFilters();
      const embeddedCount = filters.reduce((sum, filter) => sum + (filter.values?.length || 0), 0);
      if (embeddedCount === 0) {
        try {
          filters = await fetchFiltersFromCollection();
        } catch (e) {
          filters = [];
        }
      }
      phraseIndex = buildPhraseIndex(filters);
      return phraseIndex;
    })();

    try {
      return await loadingIndex;
    } finally {
      loadingIndex = null;
    }
  }

  /**
   * @param {string} query
   * @param {{ param: string, value: string, label: string, tokens: string[] }[]} index
   * @returns {{ q: string, filters: { param: string, value: string, label: string }[], original: string } | null}
   */
  function rewriteQuery(query, index) {
    const original = query.trim();
    const tokens = original.split(/\s+/).filter(Boolean);
    if (tokens.length === 0 || !index?.length) return null;

    // Stem Danish plurals/inflections on remaining (non-filter) tokens before
    // building the q value. Color inflections are handled by LABEL_ALIASES instead.
    const normalized = tokens.map(normalizeToken);
    const used = new Array(tokens.length).fill(false);
    /** @type {{ param: string, value: string, label: string }[]} */
    const matchedFilters = [];
    const matchedParams = new Set();

    for (const phrase of index) {
      if (matchedParams.has(phrase.param)) continue;
      const len = phrase.tokens.length;
      for (let i = 0; i <= normalized.length - len; i += 1) {
        let ok = true;
        for (let j = 0; j < len; j += 1) {
          if (used[i + j] || normalized[i + j] !== phrase.tokens[j]) {
            ok = false;
            break;
          }
        }
        if (!ok) continue;
        for (let j = 0; j < len; j += 1) used[i + j] = true;
        matchedFilters.push({
          param: phrase.param,
          value: phrase.value,
          label: phrase.label,
        });
        matchedParams.add(phrase.param);
        break;
      }
    }

    if (matchedFilters.length === 0) return null;

    const remaining = tokens.filter((_, i) => !used[i]);
    // remaining may be empty when the whole query is facet terms (e.g. "blå", "Sort")
    return {
      original,
      q: remaining.map(stemDanish).join(" "),
      filters: matchedFilters,
    };
  }

  /**
   * Reduce common Danish plural/inflected forms to their base so Shopify's
   * English-biased stemmer can match them.
   * Examples: toppe→top, kjoler→kjole, bukser→bukse, nederdele→nederdel
   */
  function stemDanish(word) {
    const w = word.toLowerCase();
    if (w.length > 4 && w.endsWith("ppe")) return word.slice(0, -2); // toppe→top
    if (w.length > 5 && w.endsWith("ele")) return word.slice(0, -1); // nederdele→nederdel
    // -ler, -ner, -ser, -ger: strip just the final -r (kjole+r, bluse+r)
    if (w.length > 5 && /[lnsgj]er$/.test(w)) return word.slice(0, -1);
    // -er after a consonant: strip -er, but preserve doubled consonant (jakker→jakke)
    if (w.length > 5 && w.endsWith("er") && /[^aeiouyæøå]er$/.test(w)) {
      const stem = word.slice(0, -2);
      // If the stem ends in a doubled consonant, the base form has one (jakk→jakke)
      if (/([b-df-hj-np-tv-z])\1$/.test(stem)) return stem + "e";
      return stem;
    }
    return word;
  }

  function buildFilteredSearchUrl(rewritten, { sectionId = null, includeDisplayQuery = true, collectionHandle = null } = {}) {
    const root = window.Shopify?.routes?.root || "/";
    const url = new URL(`${root}search`, window.location.origin);
    // Shopify /search with an empty q returns no results.
    // Use the wildcard "*" so all products are returned, then the facets narrow by color.
    // For drawer requests keep the original term so predictive search gets real results.
    const q = rewritten.q || (sectionId ? rewritten.original : "*");
    url.searchParams.set("q", q);
    url.searchParams.set("type", "product");
    for (const filter of rewritten.filters) {
      url.searchParams.append(filter.param, filter.value);
    }
    if (collectionHandle) {
      url.searchParams.set("search_collection", collectionHandle);
    }
    if (sectionId) {
      url.searchParams.set("section_id", sectionId);
    }
    if (includeDisplayQuery) {
      try {
        sessionStorage.setItem(DISPLAY_QUERY_KEY, rewritten.original);
      } catch (e) {
        /* ignore */
      }
    }
    return url;
  }

  function collectionPath(collection) {
    const raw = collection?.url || "";
    try {
      return new URL(raw, window.location.origin).pathname;
    } catch (e) {
      return raw.split("?")[0];
    }
  }

  function collectionHandleFrom(collection) {
    const path = collectionPath(collection);
    const part = path.split("/collections/")[1] || "";
    return decodeURIComponent(part.split("/")[0].split("?")[0] || "");
  }

  function titlesLooselyMatch(query, title) {
    const a = normalizeLabel(query);
    const b = normalizeLabel(title);
    if (!a || !b) return false;
    if (a === b) return true;
    // Simple Danish plural variants: top/toppe, kjole/kjoler
    if (a + "r" === b || b + "r" === a) return true;
    if (a + "e" === b || b + "e" === a) return true;
    if (a + "er" === b || b + "er" === a) return true;
    if (a + "pe" === b || b + "pe" === a) return true; // top/toppe
    return false;
  }

  /**
   * Prefer the same collection predictive search would surface for a category term.
   * @param {string} query
   * @returns {Promise<{ title: string, url: string, handle?: string } | null>}
   */
  async function findMatchingCollection(query) {
    const q = query.trim();
    if (!q) return null;

    const root = window.Shopify?.routes?.root || "/";
    const suggestUrl = `${root}search/suggest.json?q=${encodeURIComponent(q)}` + "&resources[type]=collection&resources[limit]=10&resources[limit_scope]=each";

    try {
      const data = await (await fetch(suggestUrl)).json();
      const collections = data?.resources?.results?.collections || [];
      if (!collections.length) return null;

      const exact = collections.find((collection) => titlesLooselyMatch(q, collection.title));
      if (exact) return exact;

      const byHandle = collections.find((collection) => {
        const handle = collectionHandleFrom(collection);
        return titlesLooselyMatch(q, handle.replace(/-/g, " ")) || titlesLooselyMatch(q, handle);
      });
      return byHandle || null;
    } catch (e) {
      return null;
    }
  }

  function buildPlainSearchUrl(q, { original = q, filters = [], collectionHandle = null } = {}) {
    const root = window.Shopify?.routes?.root || "/";
    const url = new URL(`${root}search`, window.location.origin);
    url.searchParams.set("q", q || "*");
    url.searchParams.set("type", "product");
    for (const filter of filters) {
      url.searchParams.append(filter.param, filter.value);
    }
    if (collectionHandle) {
      url.searchParams.set("search_collection", collectionHandle);
    }
    try {
      sessionStorage.setItem(DISPLAY_QUERY_KEY, original);
    } catch (e) {
      /* ignore */
    }
    return url;
  }

  function isThemeSearchForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.id === "predictive-search-form") return true;
    if (form.getAttribute("role") !== "search") return false;
    if (!form.querySelector(".search-form__input, .search-drawer__input")) return false;
    const action = form.getAttribute("action") || "";
    return action.includes("search");
  }

  async function handleSearchSubmit(form) {
    const qInput = form.elements.namedItem("q");
    if (!(qInput instanceof HTMLInputElement) && !(qInput instanceof HTMLTextAreaElement)) {
      return false;
    }

    const original = qInput.value.trim();
    if (!original) return false;

    const index = await ensurePhraseIndex();
    const rewritten = rewriteQuery(original, index);
    const filters = rewritten?.filters || [];

    // Pure filter-only query (e.g. "blå", "Sort") — use wildcard q=* on /search
    // so the search page renders and displays the color term correctly.
    if (rewritten && rewritten.q === "") {
      window.location.assign(buildFilteredSearchUrl(rewritten).toString());
      return true;
    }

    // Prefer a matching collection for the category part (or full query) so results
    // match catalog membership (e.g. "toppe", "blå toppe" → Toppe collection).
    const categoryQuery = rewritten?.q || original.split(/\s+/).map(stemDanish).join(" ");
    const collection = (await findMatchingCollection(categoryQuery)) || (await findMatchingCollection(original)) || null;
    const collectionHandle = collection ? collectionHandleFrom(collection) : null;

    if (rewritten) {
      window.location.assign(buildFilteredSearchUrl(rewritten, { collectionHandle }).toString());
      return true;
    }

    if (collectionHandle) {
      window.location.assign(
        buildPlainSearchUrl(categoryQuery, {
          original,
          filters,
          collectionHandle,
        }).toString(),
      );
      return true;
    }

    // No filter/collection match — stem Danish plurals for the search request
    // (toppe→top) but keep the original wording in the search bar.
    const stemmed = original.split(/\s+/).map(stemDanish).join(" ");
    if (stemmed !== original) {
      window.location.assign(buildPlainSearchUrl(stemmed, { original }).toString());
      return true;
    }
    return false;
  }

  function restoreDisplayQuery() {
    const input = document.querySelector('.shopify-section--main-search .search-form__input, .shopify-section--main-search input[name="q"]');
    if (!(input instanceof HTMLInputElement)) return;

    let stored = null;
    try {
      stored = sessionStorage.getItem(DISPLAY_QUERY_KEY);
      sessionStorage.removeItem(DISPLAY_QUERY_KEY);
    } catch (e) {
      stored = null;
    }
    if (stored) {
      input.value = stored;
    }
  }

  /**
   * When search_collection + filters are set, Liquid cannot facet the
   * collection (Shopify only applies filter.* to the primary search resource).
   * The grid is left empty until this fetch swaps in filtered collection HTML.
   */
  async function hydrateSearchCollectionProducts() {
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('search_collection');
    if (!handle) return;

    const section = document.querySelector('.shopify-section--main-search');
    const localInner = section?.querySelector('[data-search-collection-hydrate]');
    if (!localInner) return;

    const fallback = section.querySelector('[data-search-collection-fallback]');
    const localCount = section.querySelector('[data-search-results-count]');
    const showFallback = () => {
      if (fallback instanceof HTMLTemplateElement) {
        localInner.replaceChildren(fallback.content.cloneNode(true));
      }
      if (localCount) {
        localCount.textContent = '';
        localCount.removeAttribute('aria-busy');
      }
      localInner.removeAttribute('aria-busy');
    };

    const root = window.Shopify?.routes?.root || '/';
    const collUrl = new URL(`${root}collections/${encodeURIComponent(handle)}`, window.location.origin);
    for (const [key, value] of params.entries()) {
      if (key === 'q' || key === 'type' || key === 'search_collection' || key === 'section_id') continue;
      collUrl.searchParams.append(key, value);
    }

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      const html = await (await fetch(collUrl.toString(), { credentials: 'same-origin' })).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const remoteInner = doc.querySelector('#collection .collection__inner');
      if (!remoteInner) {
        showFallback();
        return;
      }

      localInner.innerHTML = remoteInner.innerHTML;
      const remoteCount = doc.querySelector('.collection__toolbar .subheading');
      if (remoteCount && localCount && remoteCount.textContent.trim()) {
        localCount.textContent = remoteCount.textContent.trim();
      }
      localCount?.removeAttribute('aria-busy');
      localInner.removeAttribute('aria-busy');
    } catch (e) {
      showFallback();
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  function supportsPredictiveApi() {
    try {
      return JSON.parse(document.getElementById("shopify-features").textContent || "{}").predictiveSearch;
    } catch (e) {
      return true;
    }
  }

  function interceptPredictiveSearchInput() {
    if (window.__searchRewritePredictiveBound) return;
    window.__searchRewritePredictiveBound = true;

    let debounceTimer = 0;
    /** @type {AbortController | null} */
    let abortController = null;

    function predictiveElement() {
      return document.querySelector("predictive-search");
    }

    function showPlaceholder(el) {
      if (el?.shadowRoot) {
        el.shadowRoot.innerHTML = '<slot name="placeholder"></slot>';
      }
    }

    async function runPredictiveSearch(q) {
      const el = predictiveElement();
      if (!el) return;

      abortController?.abort();
      abortController = new AbortController();
      const { signal } = abortController;

      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
      try {
        const index = await ensurePhraseIndex();
        if (signal.aborted) return;

        const rewritten = rewriteQuery(q, index);
        const root = window.Shopify?.routes?.root || "/";
        const url = rewritten
          ? buildFilteredSearchUrl(rewritten, {
              sectionId: "predictive-search",
              includeDisplayQuery: false,
            })
          : new URL(`${root}search${supportsPredictiveApi() ? "/suggest" : ""}`, window.location.origin);

        if (!rewritten) {
          url.searchParams.set("q", q);
          url.searchParams.set("section_id", "predictive-search");
          url.searchParams.set("resources[limit]", "10");
          url.searchParams.set("resources[limit_scope]", "each");
        }

        const html = await (await fetch(url.toString(), { signal })).text();
        const tempDoc = new DOMParser().parseFromString(html, "text/html");
        const section = tempDoc.querySelector(".shopify-section");
        const resultsSlot = el.querySelector('[slot="results"]');
        if (!section || !resultsSlot) return;

        el.shadowRoot.innerHTML = '<slot name="results"></slot>';
        resultsSlot.replaceChildren(...document.importNode(section, true).children);
      } catch (e) {
        if (e?.name !== "AbortError") {
          /* keep placeholder / last results */
        }
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    }

    const debouncedSearch = (q) => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        runPredictiveSearch(q);
      }, 280);
    };

    document.addEventListener(
      "input",
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLTextAreaElement)) return;
        const form = input.form;
        if (!form || form.id !== "predictive-search-form" || input.name !== "q") return;

        // Stop the theme <predictive-search> listener (bubble) from fetching.
        event.stopImmediatePropagation();

        const q = input.value.trim();
        if (!q) {
          window.clearTimeout(debounceTimer);
          abortController?.abort();
          showPlaceholder(predictiveElement());
          return;
        }
        debouncedSearch(q);
      },
      true,
    );
  }

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!isThemeSearchForm(form)) return;
      event.preventDefault();
      event.stopPropagation();
      handleSearchSubmit(form).then((handled) => {
        if (!handled) {
          form.submit();
        }
      });
    },
    true,
  );

  interceptPredictiveSearchInput();
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        restoreDisplayQuery();
        hydrateSearchCollectionProducts();
      },
      { once: true },
    );
  } else {
    restoreDisplayQuery();
    hydrateSearchCollectionProducts();
  }

  // Warm the filter index so the first drawer keystroke is fast.
  ensurePhraseIndex();
})();
